import type { QueryKey, UseQueryOptions } from '@tanstack/react-query'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import type {
  GetAssetAssignmentsResponse,
  GetAssetGroupsResponse,
  GetAssetsResponse,
  GetAssetTargetsResponse,
  PayloadFindRequestParams,
} from '../services/api/api.types'
import { getParamsWithDefaults } from '../utils/payloadHelpers'
import { useGlobalState } from './useGlobalState'

type QueryOpts<T> = Omit<UseQueryOptions<T, Error, T, QueryKey>, 'queryKey'>

export function useAssetsQuery(opts?: { params?: PayloadFindRequestParams; queryOpts?: QueryOpts<GetAssetsResponse> }) {
  const params = getParamsWithDefaults(opts?.params)

  const { api } = useGlobalState()

  return useQuery({
    queryKey: ['asset-sentry', 'assets', params],
    queryFn: () => api.getAssets(params),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 10,
    ...opts?.queryOpts,
  })
}

export function useAssetGroupsQuery(opts?: {
  params?: PayloadFindRequestParams
  queryOpts?: QueryOpts<GetAssetGroupsResponse>
}) {
  const params = getParamsWithDefaults(opts?.params)

  const { api } = useGlobalState()

  return useQuery({
    queryKey: ['asset-sentry', 'asset-groups', params],
    queryFn: () => api.getAssetGroups(params),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 10,
    ...opts?.queryOpts,
  })
}

export function useAssetTrackersQuery(opts?: { params?: PayloadFindRequestParams }) {
  const params = getParamsWithDefaults(opts?.params)

  const { api } = useGlobalState()

  return useQuery({
    queryKey: ['asset-sentry', 'asset-trackers', params],
    queryFn: () => api.getAssetTrackers(params),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 10,
  })
}

export function useAssetTargetsQuery(opts?: {
  params?: PayloadFindRequestParams
  queryOpts?: QueryOpts<GetAssetTargetsResponse>
}) {
  const params = getParamsWithDefaults(opts?.params)

  const { api } = useGlobalState()

  return useQuery({
    queryKey: ['asset-sentry', 'asset-targets', params],
    queryFn: () => api.getAssetTargets(params),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 10,
    ...opts?.queryOpts,
  })
}

export function useAssetQuery(opts: { params: { id: string | null } }) {
  const { params } = opts

  const { api } = useGlobalState()

  return useQuery({
    queryKey: ['asset-sentry', 'assets', params],
    queryFn: () => api.getAsset({ id: params.id! }),
    staleTime: 1000 * 10,
    enabled: !!params.id,
  })
}

export function useAssetGroupQuery(opts: { params: { id: string | null } }) {
  const { params } = opts

  const { api } = useGlobalState()

  return useQuery({
    queryKey: ['asset-sentry', 'asset-groups', params],
    queryFn: () => api.getAssetGroup({ id: params.id! }),
    staleTime: 1000 * 10,
    enabled: !!params.id,
  })
}

export function useAssetTrackerQuery(opts: { params: { id: string | null } }) {
  const { params } = opts

  const { api } = useGlobalState()

  return useQuery({
    queryKey: ['asset-sentry', 'asset-trackers', params],
    queryFn: () => api.getAssetTracker({ id: params.id! }),
    staleTime: 1000 * 10,
    enabled: !!params.id,
  })
}

export function useAssetTargetQuery(opts: { params: { id: string | null } }) {
  const { params } = opts

  const { api } = useGlobalState()

  return useQuery({
    queryKey: ['asset-sentry', 'asset-targets', params],
    queryFn: () => api.getAssetTarget({ id: params.id! }),
    staleTime: 1000 * 10,
    enabled: !!params.id,
  })
}

export function useAssetAssignmentsQuery(opts?: {
  params?: PayloadFindRequestParams
  queryOpts?: QueryOpts<GetAssetAssignmentsResponse>
}) {
  const params = getParamsWithDefaults(opts?.params)

  const { api } = useGlobalState()

  return useQuery({
    queryKey: ['asset-sentry', 'asset-assignments', params],
    queryFn: () => api.getAssetAssignments(params),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 10,
    ...opts?.queryOpts,
  })
}

export function useAssetAssignmentQuery(opts: { params: { id: string | null } }) {
  const { params } = opts

  const { api } = useGlobalState()

  return useQuery({
    queryKey: ['asset-sentry', 'asset-assignments', params],
    queryFn: () => api.getAssetAssignment({ id: params.id! }),
    staleTime: 1000 * 10,
    enabled: !!params.id,
  })
}
